import type { AxiosInstance } from 'axios';
import { getExperiments } from '@monolith/legacy/services/features';

/**
 * Resolved A/B experiments to relay to Ankorsearch, mirroring the Segment
 * `__experiments` shape. The monolith resolves the variant (sha256) and exposes
 * it via the `experiments` meta tag; Ankorsearch applies the received assignment
 * instead of re-bucketing locally (DAT-3178). We only forward, never re-resolve.
 */
type ResolvedExperiment = { experiment_name: string; variant_name: string };

/**
 * Convert the `{ experiment_name: variant_name }` map from getExperiments() into
 * the `[{ experiment_name, variant_name }]` list expected in the query body.
 */
function getResolvedExperiments(): ResolvedExperiment[] {
  return Object.entries(getExperiments()).map(([experiment_name, variant_name]) => ({
    experiment_name,
    variant_name,
  }));
}

/**
 * Strip environment prefix from Algolia index names to get the base index name.
 * Ankorsearch expects simple index names like 'products', 'brands', etc.
 */
function stripIndexNamePrefix(indexName: string): string {
  const lastUnderscoreIndex = indexName.lastIndexOf('_');
  if (lastUnderscoreIndex !== -1 && lastUnderscoreIndex < indexName.length - 1) {
    return indexName.slice(lastUnderscoreIndex + 1);
  }
  return indexName;
}

/**
 * Transform Algolia client paths to ankorsearch paths:
 * - /1/indexes/(*)/queries -> /queries
 * - /1/indexes/{index}/facets/{facet}/query -> /{index}/facets/{facet}
 */
function transformPathForAnkorsearch(path: string): string {
  if (path.includes('/indexes/*/queries')) {
    return '/queries';
  }
  const facetMatch = /\/1\/indexes\/([^/]+)\/facets\/([^/]+)\/query/.exec(path);
  if (facetMatch) {
    const simpleIndexName = stripIndexNamePrefix(facetMatch[1]);
    return `/${simpleIndexName}/facets/${facetMatch[2]}`;
  }
  return path;
}

/**
 * Parse URL query string params into a JSON object.
 */
function parseParamsString(paramsString: string): Record<string, unknown> {
  const params: Record<string, unknown> = {};
  const urlParams = new URLSearchParams(paramsString);

  for (const [key, value] of urlParams.entries()) {
    const decodedValue = decodeURIComponent(value);
    if (decodedValue.startsWith('[') || decodedValue.startsWith('{')) {
      try {
        params[key] = JSON.parse(decodedValue);
      } catch {
        params[key] = decodedValue;
      }
    } else {
      params[key] = decodedValue;
    }
  }

  return params;
}

/**
 * Transform request body for ankorsearch (index names + params format).
 */
function transformRequestBodyForAnkorsearch(data: unknown): { requests: Array<{ indexName: string; params: Record<string, unknown> }> } | null {
  let parsedData = data;
  if (typeof data === 'string') {
    try {
      parsedData = JSON.parse(data);
    } catch {
      return null;
    }
  }

  if (!parsedData || typeof parsedData !== 'object') {
    return null;
  }

  const parsed = parsedData as { requests?: Array<{ indexName?: string; params?: string | Record<string, unknown> }> };

  if (!parsed.requests || !Array.isArray(parsed.requests) || parsed.requests.length === 0) {
    return null;
  }

  const transformedRequests = parsed.requests.map((req) => ({
    indexName: req.indexName ? stripIndexNamePrefix(req.indexName) : '',
    params: typeof req.params === 'string' ? parseParamsString(req.params) : (req.params || {}),
  }));

  return { requests: transformedRequests };
}

export type AnkorsearchRequestPayload = {
  url: string;
  method: string;
  data?: unknown;
  headers?: Record<string, string>;
};

export type SendRequestOptions = {
  withAggregations?: boolean;
};

/**
 * Send a request to ankorsearch and return the response.
 * Transforms Algolia-style request to ankorsearch format.
 * @param locale - Locale for x-client-language (e.g. from getLocale() / i18n).
 * @param options - When `withAggregations` is `false`, the flag is added to the
 *   multi-search body so Ankorsearch skips facet aggregations. When omitted, no
 *   key is sent (Ankorsearch falls back to its default of including aggregations).
 */
export async function sendRequestToAnkorsearch(
  http: AxiosInstance,
  request: AnkorsearchRequestPayload,
  locale: string,
  options?: SendRequestOptions
): Promise<{ content: string; isTimedOut: boolean; status: number }> {
  const transformedPath = transformPathForAnkorsearch(request.url);
  const isFacetSearch = transformedPath.includes('/facets/');

  // Facet search sends the body directly (no { requests: [...] } wrapper).
  // Multi-search sends { requests: [...] } which needs index name transformation.
  let data: unknown;
  if (isFacetSearch) {
    data = typeof request.data === 'string' ? JSON.parse(request.data) : request.data;
  } else {
    const transformedData = transformRequestBodyForAnkorsearch(request.data);
    if (!transformedData) {
      throw new Error('Invalid request data for Ankorsearch');
    }
    // Relay the monolith-resolved experiments so Ankorsearch applies the tracked
    // assignment (DAT-3178 brand-diversity rerank) instead of re-bucketing.
    const list_experiments = getResolvedExperiments();
    data = {
      ...transformedData,
      ...(options?.withAggregations === false ? { withAggregations: false } : {}),
      ...(list_experiments.length ? { list_experiments } : {}),
    };
  }

  const ankorsearchUrl = `/api/ankorsearch/v1${transformedPath}`;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'x-client-language': locale,
  };

  const response = await http.request({
    url: ankorsearchUrl,
    method: request.method,
    headers,
    data,
    transformResponse: [],
  });

  return {
    content: response.data,
    isTimedOut: false,
    status: response.status,
  };
}

export type AnkorsearchFiltersResponse = {
  results: Array<{
    facets?: Record<string, Record<string, number>>;
    facets_stats?: Record<string, unknown>;
    nbHits?: number;
  }>;
};

export async function sendFiltersRequestToAnkorsearch(
  http: AxiosInstance,
  data: unknown,
  locale: string
): Promise<AnkorsearchFiltersResponse> {
  const transformedData = transformRequestBodyForAnkorsearch(data);
  if (!transformedData) {
    throw new Error('Invalid request data for Ankorsearch filters');
  }

  const filtersData = {
    requests: transformedData.requests.map((req) => ({
      ...req,
      params: {
        ...req.params,
        hitsPerPage: 0,
        page: 0,
        attributesToRetrieve: [],
      },
    })),
  };

  const response = await http.request({
    url: '/api/ankorsearch/v1/filters',
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-client-language': locale,
    },
    data: filtersData,
    transformResponse: [],
  });

  return JSON.parse(response.data);
}
